/* biome-ignore-all lint/a11y/noStaticElementInteractions: r3f <mesh> elements are 3D scene primitives, not interactive HTML — the a11y rule misfires here. */

/**
 * BoardOverhead3D — Tabletop-Simulator-style top-down 3D table view.
 *
 * Built on react-three-fiber + drei + three. Mirror-symmetric two-player
 * layout taken from the canonical Wonders TTS shape:
 *
 *      [opponent deck]    [opponent token piles]
 *      ────────────────────────────────────────
 *      ─── Realm 1 (lowest tier) ──────────────
 *      ─── Realm 2 ────────────────────────────
 *      ─── Realm 3 ────────────────────────────
 *      ─── Realm 4 (mid)        opp side ↑ ────
 *                                you side ↓
 *      ─── Realm 5 ────────────────────────────
 *      ─── Realm 6 ────────────────────────────
 *      ─── Realm 7 (highest tier) ─────────────
 *      ────────────────────────────────────────
 *      [your token piles]    [your deck]
 *
 * Per-player table mat: each side's surface texture is a typed prop
 * (myMat / opponentMat) so the future custom-mat feature drops in
 * without touching the renderer. Default is a procedural wood-grain
 * material until the asset-gen pass produces tournament-aesthetic mats.
 *
 * Cards are flat planes that lift on hover. The hand stays as a 2D
 * HTML strip under the canvas — pointer events that release over the
 * canvas raycast to the realm planes for placement.
 */

import { OrbitControls, RoundedBox, Stats, useTexture } from '@react-three/drei';
import { Canvas, useFrame } from '@react-three/fiber';
import { type ReactElement, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import type { PlayerId, PublicTableState, RealmName } from '../types.js';
import { BoardModeSwitcher } from './BoardModeSwitcher.js';

/**
 * Per-realm asset paths. The realm-tile texture is shared across all 7
 * tiers (realms are visually neutral; identity is carried by the
 * gemstone). Stones are PNG sprites tinted to that realm's gem palette.
 */
const REALM_TILE_TEXTURE = '/assets/wonders/realm-tile.png';
const STONE_TEXTURE: Record<RealmName, string> = {
  Petraia: '/assets/wonders/stone-petraia.png',
  Solfera: '/assets/wonders/stone-solfera.png',
  Thalwind: '/assets/wonders/stone-thalwind.png',
  Umbrathene: '/assets/wonders/stone-umbrathene.png',
  Heliosynth: '/assets/wonders/stone-heliosynth.png',
  Boundless: '/assets/wonders/stone-boundless.png',
  Clear: '/assets/wonders/stone-clear.png',
};

/** Mat texture descriptor — accepted by every board renderer per the renderer-mat-prop rule. */
export type MatTexture = {
  /** URL to the mat image (R2-served once the catalogue lands). */
  readonly url?: string;
  /** Display tint applied multiplicatively on the mat. */
  readonly tint?: string;
};

type SelectedSource = { kind: 'hand' } | { kind: 'realm'; realm: RealmName } | null;

type Props = {
  state: PublicTableState;
  viewer: PlayerId;
  /** Viewer's selected mat. When undefined, falls back to the procedural wood material. */
  myMat?: MatTexture | undefined;
  /** Opponent's selected mat. */
  opponentMat?: MatTexture | undefined;
  /** Show fps stats overlay (off by default). */
  debug?: boolean;
  /**
   * Click-to-play handlers — pulled up to the parent so all renderers
   * share one interaction model. Click a hand card → select; click an
   * in-play card → select-with-source-realm; click a realm → play /
   * move; click a stone-dock → seize.
   */
  selectedCardId?: string | null | undefined;
  selectedSource?: SelectedSource | undefined;
  onSelectHandCard?: ((cardId: string | null) => void) | undefined;
  onClickInPlayCard?:
    | ((cardId: string, realm: RealmName, wasSelected: boolean) => void)
    | undefined;
  onPlayCardToRealm?: ((cardId: string, realm: RealmName) => void) | undefined;
  onSeizeStoneAt?: ((realmIdx: number, claim: boolean) => void) | undefined;
  /** Transient banner text (e.g. "Played Aurelia"). Cleared by parent after ~2s. */
  toast?: string | null | undefined;
};

const REALM_COUNT = 7;
const TABLE_W = 6;
const TABLE_D = 9; // depth — long axis (player↔player)
const REALM_W = 5;
const REALM_D = 0.85;
const REALM_GAP = 0.05;

export function BoardOverhead3D({
  state,
  viewer,
  myMat,
  opponentMat,
  debug,
  selectedCardId,
  selectedSource,
  onSelectHandCard,
  onClickInPlayCard,
  onPlayCardToRealm,
  onSeizeStoneAt,
  toast,
}: Props): ReactElement {
  const round = state.round ?? 0;
  const isMyTurn = state.activePlayer === viewer;
  const opponent = state.players.find((p) => p !== viewer) ?? state.players[1];

  return (
    <div className="flex h-screen w-screen flex-col bg-stone-950 text-stone-100">
      {/* Minimal HUD — competitive players want signal not chrome. */}
      <header className="flex items-center justify-between border-stone-900 border-b bg-stone-950/90 px-3 py-1.5 backdrop-blur">
        <div className="font-mono text-[11px] text-amber-300">
          R{round}/7 ·{' '}
          <span className={isMyTurn ? 'text-amber-200' : 'text-stone-500'}>
            {isMyTurn ? 'Your Turn' : "Opponent's turn"}
          </span>
        </div>
        <div className="text-[11px] text-stone-400 font-mono">
          {viewer} <span className="text-stone-600">vs</span> {opponent}
        </div>
        <BoardModeSwitcher />
      </header>

      {/* Transient action toast — shown for ~2s after every dispatched
          action so the player gets immediate feedback that their click
          registered, even if the resulting state delta lags. */}
      {toast ? (
        <div className="pointer-events-none absolute left-1/2 top-12 z-30 -translate-x-1/2 rounded-md bg-amber-700/85 px-3 py-1 text-sm text-white shadow-lg ring-1 ring-amber-300/40 backdrop-blur">
          {toast}
        </div>
      ) : null}

      {/* 3D viewport */}
      <div className="relative flex-1">
        <Canvas
          camera={{ position: [0, 7, 5.5], fov: 35, near: 0.1, far: 100 }}
          shadows
          gl={{ antialias: true, alpha: false }}
        >
          {/* Lighting — soft cinematic lift, not shadowy storybook. */}
          <ambientLight intensity={0.55} />
          <directionalLight position={[3, 8, 4]} intensity={1.1} castShadow />
          <directionalLight position={[-4, 6, -3]} intensity={0.4} color="#88aaff" />

          {/* Camera control — orbit on right-click only so left-click
              passes through cleanly to mesh onClick handlers. Without
              this remap, OrbitControls silently swallows clicks that
              moved even one pixel between pointerdown and pointerup. */}
          <OrbitControls
            enablePan={false}
            enableZoom={true}
            minDistance={6}
            maxDistance={14}
            minPolarAngle={Math.PI / 8}
            maxPolarAngle={Math.PI / 2.2}
            mouseButtons={{
              LEFT: undefined as unknown as number,
              MIDDLE: 1, // dolly
              RIGHT: 0, // rotate (THREE.MOUSE.ROTATE === 0)
            }}
            touches={{ ONE: 0, TWO: 1 }}
          />

          {/* Wooden table border + felt insets per side (mat-tinted). */}
          <TableSurface myMat={myMat} opponentMat={opponentMat} />

          {/* The 7 realm strips, mirror-symmetric across z=0. The realm
              that matches state.round receives an active-glow on its
              stone (round 1 → tier 0 / first realm, round 7 → tier 6). */}
          {Array.from({ length: REALM_COUNT }).map((_, i) => {
            const realm = state.realms[i];
            if (!realm) return null;
            const isActive = (state.round ?? 0) === i + 1;
            return (
              <RealmStrip
                key={realm.name}
                tier={i}
                tierIdx={i}
                realm={realm}
                viewer={viewer}
                opponent={opponent}
                isActive={isActive}
                isSelectable={selectedCardId !== null && selectedCardId !== undefined}
                selectedCardId={selectedCardId}
                selectedSource={selectedSource}
                onClickInPlayCard={onClickInPlayCard}
                onClickRealm={() => {
                  if (selectedCardId && onPlayCardToRealm) {
                    onPlayCardToRealm(selectedCardId, realm.name);
                  }
                }}
                onClickStone={() => {
                  if (onSeizeStoneAt) onSeizeStoneAt(i, realm.stone !== viewer);
                }}
              />
            );
          })}

          {/* Decks at far edges. */}
          <DeckPile side="opponent" count={zoneCount(state, opponent, 'deck')} />
          <DeckPile side="self" count={zoneCount(state, viewer, 'deck')} />

          {debug ? <Stats /> : null}
        </Canvas>

        {/* Click-mode prompt — competitive default is keyboard-driven so this is muted. */}
        <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded bg-stone-900/70 px-2 py-0.5 text-[10px] text-stone-400 font-mono">
          drag to orbit · scroll to zoom · drag cards from hand to a realm
        </div>
      </div>

      {/* 2D hand strip — click-to-select; click a realm to play. */}
      <Hand2DStrip
        state={state}
        viewer={viewer}
        selectedCardId={selectedCardId ?? null}
        onSelect={onSelectHandCard}
      />
    </div>
  );
}

// ─── Sub-scenes ─────────────────────────────────────────────────────

function TableSurface({
  myMat,
  opponentMat,
}: {
  myMat?: MatTexture | undefined;
  opponentMat?: MatTexture | undefined;
}): ReactElement {
  return (
    <group>
      {/* Outer wooden border. */}
      <mesh receiveShadow position={[0, -0.05, 0]}>
        <boxGeometry args={[TABLE_W + 1, 0.1, TABLE_D + 1]} />
        <meshStandardMaterial color="#3a2418" roughness={0.85} metalness={0.05} />
      </mesh>
      {/* Inner felt — split per side for per-player mats. */}
      <MatPanel side="opponent" mat={opponentMat} />
      <MatPanel side="self" mat={myMat} />
    </group>
  );
}

function MatPanel({
  side,
  mat,
}: {
  side: 'self' | 'opponent';
  mat?: MatTexture | undefined;
}): ReactElement {
  // Self side has positive z, opponent negative z.
  const z = side === 'self' ? TABLE_D / 4 : -TABLE_D / 4;
  // Procedural fallback: dark felt with a subtle directional tint per side
  // (warm amber for self, cool cyan for opponent) for orientation cues.
  const fallback = side === 'self' ? '#241813' : '#13202a';
  const tint = mat?.tint ?? fallback;
  return (
    <mesh receiveShadow position={[0, 0.001, z]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[TABLE_W, TABLE_D / 2 - 0.05]} />
      <meshStandardMaterial color={tint} roughness={0.9} metalness={0} />
    </mesh>
  );
}

function RealmStrip({
  tier,
  tierIdx,
  realm,
  viewer,
  opponent,
  isActive,
  isSelectable,
  selectedCardId,
  selectedSource,
  onClickRealm,
  onClickStone,
  onClickInPlayCard,
}: {
  tier: number;
  tierIdx: number;
  realm: {
    name: RealmName;
    placements: readonly {
      cardId: string;
      owner: PlayerId;
      tapped: boolean;
      faceDown: boolean;
    }[];
    stone: PlayerId | null;
  };
  viewer: PlayerId;
  opponent: PlayerId;
  isActive: boolean;
  /** True when a hand card is selected — realm tile glows as a click target. */
  isSelectable: boolean;
  /** Currently-selected card id (hand or in-play) — used to glow the right card. */
  selectedCardId: string | null | undefined;
  /** Where the selection originated. */
  selectedSource: { kind: 'hand' | 'realm'; realm?: RealmName } | null | undefined;
  onClickRealm: () => void;
  onClickStone: () => void;
  /** Click handler on an in-play card. `wasSelected` reflects the click-target's selection state at click time. */
  onClickInPlayCard?:
    | ((cardId: string, realm: RealmName, wasSelected: boolean) => void)
    | undefined;
}): ReactElement {
  // Lay realms out along z so tier 0 is far (opponent's nearest realm) and
  // tier 6 is close to camera. Realm order matches the canonical TTS shape.
  const z =
    -((REALM_COUNT / 2) * (REALM_D + REALM_GAP)) + tier * (REALM_D + REALM_GAP) + REALM_D / 2;
  const stoneOwner = realm.stone;

  // Stone position: center when uncontested (the realm's neutral home);
  // moves to the seizing player's inner-edge dock when claimed.
  const stoneX =
    stoneOwner === viewer
      ? REALM_W / 4 // your dock, right of center
      : stoneOwner === opponent
        ? -REALM_W / 4 // opponent dock, left of center
        : 0; // neutral center

  // Realm-tile texture, shared by all 7 tiers — the gemstone carries the
  // realm identity, not the playmat itself.
  const tileTexture = useTexture(REALM_TILE_TEXTURE);

  // Suppress unused-prop warnings for tierIdx — kept on the props for
  // future per-tier overrides (animation timing variation, etc.).
  void tierIdx;

  return (
    <group position={[0, 0.011, z]}>
      {/* Realm tile (textured + clickable). When a hand card is selected,
          the tile pulses amber as a click target — playing the card to
          the viewer's side of this realm. stopPropagation keeps stone
          clicks distinct. */}
      <RoundedBox
        args={[REALM_W, 0.02, REALM_D]}
        radius={0.03}
        smoothness={3}
        castShadow={false}
        onClick={(e) => {
          e.stopPropagation();
          onClickRealm();
        }}
      >
        <meshStandardMaterial
          map={tileTexture}
          roughness={0.85}
          metalness={0.05}
          emissive={isSelectable ? '#fbbf24' : '#000000'}
          emissiveIntensity={isSelectable ? 0.18 : 0}
        />
      </RoundedBox>

      {/* Realm stone — PNG sprite at the (animated) owner-derived position.
          Clicking triggers seize/release. */}
      <RealmStone
        realmName={realm.name}
        position={[stoneX, 0.04, 0]}
        active={isActive}
        onClick={onClickStone}
      />

      {/* Per-side player docks. Larger inner-edge ring → click-through
          target for seize, also tinted by ownership. */}
      <mesh
        position={[REALM_W / 4, 0.022, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={(e) => {
          e.stopPropagation();
          onClickStone();
        }}
      >
        <ringGeometry args={[0.1, 0.16, 24]} />
        <meshBasicMaterial
          color={stoneOwner === viewer ? '#fbbf24' : '#52525b'}
          transparent
          opacity={0.7}
        />
      </mesh>
      <mesh
        position={[-REALM_W / 4, 0.022, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={(e) => {
          e.stopPropagation();
          onClickStone();
        }}
      >
        <ringGeometry args={[0.1, 0.16, 24]} />
        <meshBasicMaterial
          color={stoneOwner === opponent ? '#fb7185' : '#52525b'}
          transparent
          opacity={0.7}
        />
      </mesh>

      {/* Cards laid out across the strip. Self side z+0.18, opponent z-0.18.
          Key is realm + cardId + owner. */}
      {realm.placements.map((p) => {
        const idx = realm.placements.indexOf(p);
        const slotX = -REALM_W / 2 + 0.7 + (idx % 5) * 0.42;
        const slotZ = p.owner === viewer ? 0.18 : -0.18;
        const isSelected =
          selectedCardId === p.cardId &&
          selectedSource?.kind === 'realm' &&
          selectedSource.realm === realm.name;
        const ownedHere = p.owner === viewer;
        return (
          <CardMesh
            key={`${realm.name}-${p.owner}-${p.cardId}`}
            cardId={p.cardId}
            position={[slotX, 0.02, slotZ]}
            tapped={p.tapped}
            faceDown={p.faceDown}
            ownedByViewer={ownedHere}
            selected={isSelected}
            selectable={ownedHere}
            onClick={
              ownedHere ? () => onClickInPlayCard?.(p.cardId, realm.name, isSelected) : undefined
            }
          />
        );
      })}
    </group>
  );
}

/**
 * RealmStone — a small disc sprite carrying the realm's gemstone PNG.
 * Smoothly damps to a target position when the stone changes owners.
 * Active-glow pulses via a sin-driven emissiveIntensity when this is the
 * round's currently-active realm.
 */
function RealmStone({
  realmName,
  position,
  active,
  onClick,
}: {
  realmName: RealmName;
  position: [number, number, number];
  active: boolean;
  onClick?: ((e: { stopPropagation: () => void }) => void) | undefined;
}): ReactElement {
  const meshRef = useRef<THREE.Mesh>(null);
  const matRef = useRef<THREE.MeshStandardMaterial>(null);
  const tex = useTexture(STONE_TEXTURE[realmName]);
  // The stone PNG is rendered onto a small disc (a thin cylinder) so it
  // catches scene lighting alongside the rest of the table.
  const radius = useMemo(() => 0.13, []);

  useFrame((_state, delta) => {
    const m = meshRef.current;
    if (m) {
      m.position.x = THREE.MathUtils.damp(m.position.x, position[0], 6, delta);
    }
    const mat = matRef.current;
    if (mat) {
      // Active realm: emissive pulse 0.3 → 0.9 over ~1.5s.
      const target = active ? 0.45 + Math.sin(_state.clock.elapsedTime * 4) * 0.3 : 0;
      mat.emissiveIntensity = THREE.MathUtils.damp(mat.emissiveIntensity, target, 8, delta);
    }
  });

  return (
    <mesh
      ref={meshRef}
      position={position}
      castShadow
      onClick={(e) => {
        e.stopPropagation();
        onClick?.(e);
      }}
    >
      <cylinderGeometry args={[radius, radius * 1.05, 0.04, 32]} />
      <meshStandardMaterial
        ref={matRef}
        map={tex}
        emissiveMap={tex}
        emissive="#ffffff"
        emissiveIntensity={0}
        roughness={0.45}
        metalness={0.35}
      />
    </mesh>
  );
}

function CardMesh({
  cardId,
  position,
  tapped,
  faceDown,
  ownedByViewer,
  selected,
  selectable,
  onClick,
}: {
  cardId: string;
  position: [number, number, number];
  tapped: boolean;
  faceDown: boolean;
  ownedByViewer: boolean;
  selected: boolean;
  /** True when clicking this card would do something (own card + click-to-move flow available). */
  selectable: boolean;
  onClick?: (() => void) | undefined;
}): ReactElement {
  const meshRef = useRef<THREE.Mesh>(null);
  const matRef = useRef<THREE.MeshStandardMaterial>(null);
  const [hovered, setHovered] = useState(false);
  const cardBack = useTexture('/assets/wonders/card-back.png');

  useFrame((_state, delta) => {
    const m = meshRef.current;
    if (!m) return;
    // Lift order: selected (highest) > hovered > resting.
    const target = selected ? 0.42 : hovered && selectable ? 0.22 : 0.02;
    m.position.y = THREE.MathUtils.damp(m.position.y, target, 8, delta);
    // Tap rotation around Y. Face-down cards retain tap rotation.
    const targetRot = tapped ? Math.PI / 2 : 0;
    m.rotation.y = THREE.MathUtils.damp(m.rotation.y, targetRot, 6, delta);
    // Selection glow.
    const mat = matRef.current;
    if (mat) {
      const glow = selected ? 0.5 : 0;
      mat.emissiveIntensity = THREE.MathUtils.damp(mat.emissiveIntensity, glow, 8, delta);
    }
  });

  // Until per-card art textures are wired, every card uses the card-back
  // texture on top + a tinted side colour for ownership orientation.
  // When card-art lands, the top face material gets per-cardId useTexture.
  const sideColor = ownedByViewer ? '#a87f47' : '#3f5e7a';
  void cardId;

  return (
    <mesh
      ref={meshRef}
      position={position}
      castShadow
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      <boxGeometry args={[0.36, 0.015, 0.5]} />
      {/* The box has 6 face materials (right, left, top, bottom, front,
          back). Card-back goes on top + bottom (face-down looks the same
          from above and below). Sides use the tinted ownership colour. */}
      <meshStandardMaterial
        ref={matRef}
        attach="material-0"
        color={sideColor}
        roughness={0.7}
        metalness={0.1}
        emissive={selected ? '#fbbf24' : '#000000'}
        emissiveIntensity={0}
      />
      <meshStandardMaterial attach="material-1" color={sideColor} roughness={0.7} />
      <meshStandardMaterial
        attach="material-2"
        map={cardBack}
        color={faceDown ? '#ffffff' : '#cfa86b'}
        roughness={0.55}
      />
      <meshStandardMaterial attach="material-3" map={cardBack} color="#ffffff" roughness={0.55} />
      <meshStandardMaterial attach="material-4" color={sideColor} roughness={0.7} />
      <meshStandardMaterial attach="material-5" color={sideColor} roughness={0.7} />
    </mesh>
  );
}

function DeckPile({ side, count }: { side: 'self' | 'opponent'; count: number }): ReactElement {
  const z = side === 'self' ? TABLE_D / 2 - 0.5 : -(TABLE_D / 2 - 0.5);
  const x = TABLE_W / 2 - 0.5;
  const stack = Math.min(40, Math.max(1, count));
  const height = stack * 0.008;
  return (
    <mesh position={[x, height / 2 + 0.02, z]} castShadow>
      <boxGeometry args={[0.36, height, 0.5]} />
      <meshStandardMaterial color="#1a1a1f" roughness={0.6} />
    </mesh>
  );
}

function Hand2DStrip({
  state,
  viewer,
  selectedCardId,
  onSelect,
}: {
  state: PublicTableState;
  viewer: PlayerId;
  selectedCardId: string | null | undefined;
  onSelect: ((cardId: string | null) => void) | undefined;
}): ReactElement {
  const zones = state.zones[viewer];
  const hand = zones && 'hand' in zones ? (zones.hand as readonly string[]) : [];
  return (
    <div className="border-stone-900 border-t bg-stone-950/90 px-3 py-2 backdrop-blur">
      <div className="mb-1 text-center text-[10px] text-stone-500 font-mono">
        {selectedCardId
          ? `↑ Click a realm to play "${selectedCardId.slice(0, 14)}", or click the card again to deselect.`
          : 'Click a card to select; click a realm to play it; click a stone-dock to seize.'}
      </div>
      <div className="flex items-end justify-center gap-1 overflow-x-auto">
        {hand.length === 0 ? (
          <span className="text-[11px] text-stone-500 italic">No cards in hand.</span>
        ) : (
          hand.map((cardId) => {
            const selected = cardId === selectedCardId;
            return (
              <button
                key={cardId}
                type="button"
                onClick={() => onSelect?.(selected ? null : cardId)}
                className={`h-16 w-12 flex-shrink-0 rounded-sm border bg-gradient-to-br p-1 text-[8px] font-mono leading-tight transition ${
                  selected
                    ? '-translate-y-2 scale-110 border-amber-300 from-amber-500 to-amber-800 text-amber-50 ring-2 ring-amber-300/60'
                    : 'border-amber-700/60 from-amber-800 to-amber-950 text-amber-100 hover:scale-105'
                }`}
                title={cardId}
              >
                {cardId.slice(0, 14)}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────

function zoneCount(state: PublicTableState, player: PlayerId, zone: 'deck' | 'abyss'): number {
  const zones = state.zones[player];
  if (!zones) return 0;
  if (zone in zones && Array.isArray((zones as Record<string, unknown>)[zone])) {
    return ((zones as Record<string, unknown>)[zone] as readonly unknown[]).length;
  }
  // Redacted opponent view — handCount/deckCount fields.
  const compact = zones as { deckCount?: number };
  if (zone === 'deck' && typeof compact.deckCount === 'number') return compact.deckCount;
  return 0;
}
